import { useMemo, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList,
} from "recharts";

// ─── Constants ───────────────────────────────────────────────
const SEVERITY_COLORS = {
  critical: "#D32F2F",
  high: "#F57C00",
  warning: "#ED6C02",
  info: "#0288D1",
};

const STATUS_COLORS = {
  active: "#D32F2F",
  acknowledged: "#F57C00",
  cleared: "#2E7D32",
};

// ─── Helpers ─────────────────────────────────────────────────
const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const formatMinutes = (mins) => {
  if (!mins || isNaN(mins)) return "—";
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// ─── Stat Card ───────────────────────────────────────────────
function StatCard({ label, value, color, sub }) {
  return (
    <Paper
      sx={{
        p: 2.5,
        flex: "1 1 130px",
        minWidth: 120,
        maxWidth: {
          xs: "100%",
          md: 125
        },
        borderRadius: 2,
        border: "1px solid #E5E7EB",
        background: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
          transform: "translateY(-3px)",
        },
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={600} sx={{ color, mt: 0.5, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

// ─── Section wrapper ─────────────────────────────────────────
// ✅ FIX: Added height="100%" and display flex so it stretches inside Grid items
function Section({ title, subtitle, children }) {
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        height: "100%",         // ✅ stretches to match sibling Grid item height
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

// ─── Empty state ─────────────────────────────────────────────
function EmptyChart({ message = "No data available" }) {
  return (
    <Box
      height={200}
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ border: "1px dashed #e0e0e0", borderRadius: 1 }}
    >
      <Typography variant="body2" color="text.disabled">
        {message}
      </Typography>
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────
export default function AnalyticsTab({ alerts = [] }) {
  const [freqRange, setFreqRange] = useState(7);

  // ── 1. Stat counts ────────────────────────────────────────
  const counts = useMemo(() => ({
    total: alerts.length,
    active: alerts.filter((a) => a.status === "active").length,
    acknowledged: alerts.filter((a) => a.status === "acknowledged").length,
    cleared: alerts.filter((a) => a.status === "cleared").length,
  }), [alerts]);

  // ── 2. Alert frequency over time ─────────────────────────
  const frequencyData = useMemo(() => {
    const days = freqRange;
    const buckets = {};
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split("T")[0];
      buckets[key] = { date: key, count: 0 };
    }
    alerts.forEach((a) => {
      const key = new Date(a.triggered_at).toISOString().split("T")[0];
      if (buckets[key]) buckets[key].count += 1;
    });
    return Object.values(buckets).map((b) => ({ ...b, label: formatDate(b.date) }));
  }, [alerts, freqRange]);

  // ── 3. Severity breakdown (donut) ────────────────────────
  const severityData = useMemo(() => {
    const c = { critical: 0, high: 0, warning: 0, info: 0 };
    alerts.forEach((a) => { if (c[a.severity] !== undefined) c[a.severity]++; });
    return Object.entries(c).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [alerts]);

  // ── 4. Top triggered rules ────────────────────────────────
  const topRulesData = useMemo(() => {
    const map = {};
    alerts.forEach((a) => {
      const key = a.metric_key || "unknown";
      if (!map[key]) map[key] = { metric: key, count: 0, critical: 0 };
      map[key].count += 1;
      if (a.severity === "critical") map[key].critical += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [alerts]);

  // ── 5. MTTR calculation ───────────────────────────────────
  const mttrData = useMemo(() => {
    const cleared = alerts.filter((a) => a.status === "cleared" && a.cleared_at && a.triggered_at);
    if (cleared.length === 0) return { avg: null, fastest: null, slowest: null, count: 0 };
    const durations = cleared.map((a) => (new Date(a.cleared_at) - new Date(a.triggered_at)) / 60000);
    const avg = durations.reduce((s, d) => s + d, 0) / durations.length;
    return { avg, fastest: Math.min(...durations), slowest: Math.max(...durations), count: cleared.length };
  }, [alerts]);

  const mttrByDay = useMemo(() => {
    const map = {};
    alerts
      .filter((a) => a.status === "cleared" && a.cleared_at)
      .forEach((a) => {
        const key = new Date(a.triggered_at).toISOString().split("T")[0];
        if (!map[key]) map[key] = { date: key, total: 0, count: 0 };
        const mins = (new Date(a.cleared_at) - new Date(a.triggered_at)) / 60000;
        map[key].total += mins;
        map[key].count += 1;
      });
    return Object.values(map)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({ label: formatDate(d.date), avg: Math.round(d.total / d.count) }));
  }, [alerts]);

  // ── 6. Status over time (stacked bar) ────────────────────
  const statusOverTime = useMemo(() => {
    const map = {};
    alerts.forEach((a) => {
      const key = new Date(a.triggered_at).toISOString().split("T")[0];
      if (!map[key]) map[key] = { date: key, active: 0, acknowledged: 0, cleared: 0 };
      if (map[key][a.status] !== undefined) map[key][a.status]++;
    });
    return Object.values(map)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-14)
      .map((d) => ({ ...d, label: formatDate(d.date) }));
  }, [alerts]);

  // ─── Render ───────────────────────────────────────────────
  return (
    <Box width="100%" display="flex" flexDirection="column" gap={2}>

      {/* ═══ ROW 1: STAT CARDS ════════════════════════════════ */}
      <Box display="flex" gap={2} flexWrap="wrap">
        <StatCard label="Total Alerts" value={counts.total} color="text.primary" />
        <StatCard label="Active" value={counts.active} color="#D32F2F"
          sub={counts.active > 0 ? "Needs attention" : "All clear"} />
        <StatCard label="Acknowledged" value={counts.acknowledged} color="#F57C00" />
        <StatCard label="Cleared" value={counts.cleared} color="#2E7D32"
          sub={counts.total > 0
            ? `${Math.round((counts.cleared / counts.total) * 100)}% resolution rate`
            : undefined}
        />
        <StatCard
          label="Avg Resolution"
          value={formatMinutes(mttrData.avg)}
          color="#1565C0"
          sub={mttrData.count > 0 ? `from ${mttrData.count} resolved alerts` : "No resolved alerts"}
        />
      </Box>


      {/* ═══ ROW 2: FREQUENCY + SEVERITY ════════════════════ */}
      <Box display="flex" sx={{ flexDirection: { xs: "column", md: "row" } }} gap={2} width="100%" alignItems="stretch">

        <Box flex="1 1 0" width={"100%"}>
          <Section title="Alert Frequency" subtitle="Number of alerts triggered per day">
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <ToggleButtonGroup
                value={freqRange}
                exclusive
                size="small"
                onChange={(_, v) => v && setFreqRange(v)}
              >
                {[7, 14, 30].map((d) => (
                  <ToggleButton key={d} value={d} sx={{ px: 1.5, fontSize: 11 }}>
                    {d}d
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            {frequencyData.every((d) => d.count === 0) ? (
              <EmptyChart message="No alerts in this period" />
            ) : (
              <Box height={200}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={frequencyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RechartsTooltip formatter={(v) => [v, "Alerts"]} labelFormatter={(l) => `Date: ${l}`} />
                    <Bar dataKey="count" fill="#1976d2" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="count" position="top" style={{ fontSize: 10, fill: "#555" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Section>
        </Box>

        <Box flex="1 1 0" width={"100%"}>
          <Section title="Severity Breakdown" subtitle="Distribution across all alerts">
            {severityData.length === 0 ? (
              <EmptyChart message="No alert data" />
            ) : (
              <Box height={200}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={severityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {severityData.map((entry) => (
                        <Cell key={entry.name} fill={SEVERITY_COLORS[entry.name] || "#999"} />
                      ))}
                    </Pie>
                    <Legend formatter={(value) => (
                      <span style={{ fontSize: 12, textTransform: "capitalize" }}>{value}</span>
                    )} />
                    <RechartsTooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Section>
        </Box>
      </Box>

      {/* ═══ ROW 3: TOP RULES + MTTR ═════════════════════════ */}
      <Box display="flex" sx={{ flexDirection: { xs: "column", md: "row" } }} gap={2} width="100%" alignItems="stretch">

        <Box flex="1 1 0" width={"100%"}>
          <Section title="Top Triggered Metrics" subtitle="Which metrics fire alerts most often">
            {topRulesData.length === 0 ? (
              <EmptyChart message="No alert data" />
            ) : (
              <Box height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topRulesData}
                    layout="vertical"
                    margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="metric" tick={{ fontSize: 11 }} width={80} />
                    <RechartsTooltip formatter={(v, name) => [v, name === "count" ? "Total" : "Critical"]} />
                    <Bar dataKey="count" fill="#1976d2" radius={[0, 3, 3, 0]} name="Total">
                      <LabelList dataKey="count" position="right" style={{ fontSize: 10, fill: "#555" }} />
                    </Bar>
                    <Bar dataKey="critical" fill="#D32F2F" radius={[0, 3, 3, 0]} name="Critical" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Section>
        </Box>

        <Box flex="1 1 0" width={"100%"}>
          <Section title="Resolution Time (MTTR)" subtitle="Average time from trigger to cleared">
            <Box display="flex" gap={2} mb={2}>
              {[
                { label: "Average", value: formatMinutes(mttrData.avg) },
                { label: "Fastest", value: formatMinutes(mttrData.fastest) },
                { label: "Slowest", value: formatMinutes(mttrData.slowest) },
              ].map(({ label, value }) => (
                <Box key={label} flex={1} textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{value}</Typography>
                </Box>
              ))}
            </Box>
            {mttrByDay.length < 2 ? (
              <EmptyChart message="Need more resolved alerts for trend" />
            ) : (
              <Box height={120}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mttrByDay} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip formatter={(v) => [`${v} min`, "Avg Resolution"]} />
                    <Line
                      type="monotone"
                      dataKey="avg"
                      stroke="#1565C0"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Section>
        </Box>
      </Box>

      {/* ═══ ROW 4: STATUS OVER TIME (STACKED BAR) ═══════════ */}
      <Section
        title="Alert Status Over Time"
        subtitle="Daily breakdown of active / acknowledged / cleared — shows if your team is keeping up"
      >
        {statusOverTime.length === 0 ? (
          <EmptyChart message="No alert history yet" />
        ) : (
          <Box height={220}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusOverTime} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <RechartsTooltip />
                <Legend formatter={(value) => (
                  <span style={{ fontSize: 12, textTransform: "capitalize" }}>{value}</span>
                )} />
                <Bar dataKey="active" name="Active" stackId="a" fill={STATUS_COLORS.active} />
                <Bar dataKey="acknowledged" name="Acknowledged" stackId="a" fill={STATUS_COLORS.acknowledged} />
                <Bar dataKey="cleared" name="Cleared" stackId="a" fill={STATUS_COLORS.cleared} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Section>

    </Box>
  );
}
